"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const common_1 = require("../common");
const CreaseEdge_1 = require("../core/CreaseEdge");
// AdobePatentID="P8499-US-ORG1"
/**
 * A helper class used to represent one or more merged edges for analysis.
 * @private
 */
class MetaEdge extends CreaseEdge_1.CreaseEdge {
    constructor(index, edge, vertex1, vertex2) {
        super(index, vertex1, vertex2, edge.assignment, edge.controlPoints, edge.foldAngle);
        this._edges = [edge];
        // This is a 2D array of edges that gives information about
        // parallel/serial connectivity of edges withing this MetaEdge.
        this._parallelEdges = [[edge]];
        // Store corresponding edge lengths.
        this._parallelEdgeLengths = [edge.length];
        // In case there are >2 faces connected to this edge, tab faces are demoted
        // from this.leftFace, this.rightFace to this.tabFaces during the merging process.
        this._tabFaces = [];
        this._isDestroyed = false;
    }
    /**
     * Sets the index of this edge.
     */
    set index(index) {
        this._index = index;
    }
    /**
     * Returns the first vertex of this edge.
     */
    get vertex1() {
        return super.vertex1;
    }
    /**
     * Returns the first vertex of this edge.
     */
    get vertex2() {
        return super.vertex2;
    }
    /**
     * Returns the left face of this edge, or undefined.
     */
    get leftFace() {
        return super.leftFace;
    }
    /**
     * Returns the right face of this edge, or undefined.
     */
    get rightFace() {
        return super.rightFace;
    }
    /**
     * Returns a flat array of CreaseEdges contained in this MetaEdge.
     * @returns {CreaseEdge[]} An array of edges contained in this MetaEdge.
     */
    get edges() {
        return this._edges;
    }
    /**
     * Returns a 2D array of CreaseEdges contained in this MetaEdge.  At the top level, it is
     * as array of edges in parallel.  Note that each of the parallel edges may be a single
     * edge [edge], or an array of edges in series [edge1, edge2, edge3].
     * @returns {CreaseEdge[][]} A 2D array of edges contained in this MetaEdge.
     */
    get parallelEdges() {
        return this._parallelEdges;
    }
    /**
     * Returns the current tabFaces for this edge.
     * In case there are >2 faces connected to this edge, tab faces are demoted
     * from this.leftFace, this.rightFace to this.tabFaces during the merging process.
     * @returns {MetaFace[]} An array of tab faces.
     */
    get tabFaces() {
        return this._tabFaces;
    }
    /**
     * Returns an array of MetaFaces incident to this MetaEdge, including tab faces.
     * @returns {MetaFace[]} An array of faces incident to this MetaEdge.
     */
    get incidentFaces() {
        const incidentFaces = [];
        if (this.leftFace) {
            incidentFaces.push(this.leftFace);
        }
        if (this.rightFace && this.rightFace !== this.leftFace) {
            incidentFaces.push(this.rightFace);
        }
        return incidentFaces.concat(this.tabFaces);
    }
    /**
     * Returns the average length of the wrapped edges.
     * @returns {number} Average edge length.
     */
    get length() {
        return common_1.utils.average(this._parallelEdgeLengths);
    }
    get isDestroyed() {
        return this._isDestroyed;
    }
    // TODO: commenting this out causess an error?
    // /**
    //  * Set rightFace.
    //  * Only called from MetaEdge.splitParallel().
    //  */
    // set rightFace(face) {
    //     this._rightFace = face;
    // }
    // /**
    //  * Set leftFace.
    //  * Only called from MetaEdge.splitParallel().
    //  */
    // set leftFace(face) {
    //     this._leftFace = face;
    // }
    hasSameVertices(edge) {
        if (edge.vertex1 === this.vertex1 && edge.vertex2 === this.vertex2) {
            return true;
        }
        return edge.vertex1 === this.vertex2 && edge.vertex2 === this.vertex1;
    }
    /**
     * Returns the CreaseEdge adjacent to a given CreaseFace.
     * @param {CreaseFace} face - A face.
     * @param {MetaVertex} vertex - An adjacent MetaVertex.
     * @returns {CreaseEdge} Edge adjacent to input face.
     */
    getEdgeAdjacentToFaceAtVertex(face, vertex) {
        let adjacentEdge;
        this.parallelEdges.forEach((parallelEdge) => {
            let edge = parallelEdge[0];
            if (parallelEdge.length > 1) {
                // Pick the edge with a vertex inside vertex.vertices.
                // It's either the first or last vertex in the list.
                const { vertices } = vertex;
                if (vertices.indexOf(edge.vertex1) < 0) {
                    if (vertices.indexOf(edge.vertex2) < 0) {
                        edge = parallelEdge[parallelEdge.length - 1];
                        if (vertices.indexOf(edge.vertex1) < 0
                            && vertices.indexOf(edge.vertex2) < 0) {
                            throw new Error(`Can't find an edge in ${parallelEdge.map(e => e.index)}
                                corresponding to vertex: ${vertex.toString()}`);
                        }
                    }
                }
            }
            if (edge.rightFace === face && edge.leftFace === face) {
                throw new Error(`Edge adjacent to face ${face.index} on both sides.`);
            }
            if (edge.rightFace === face || edge.leftFace === face) {
                if (adjacentEdge) {
                    throw new Error(`Multiple edges adjacent to face ${face.index}.`);
                }
                adjacentEdge = edge;
            }
        });
        if (!adjacentEdge) {
            throw new Error(`No edge adjacent to face ${face.index}`);
        }
        return adjacentEdge;
    }
    /**
     * Merges two MetaEdges in parallel.
     * @param {MetaEdge} edge An edge to merge to this one.
     * Passed in edge is destroyed upon completion of merge.
     */
    mergeParallel(edge) {
        // First check that vertices are the same.
        if (!this.hasSameVertices(edge)) {
            throw new Error('Unable to parallel merge two MetaEdges: incompatible vertices.');
        }
        // Edges need same orientation.
        if (edge.vertex1 === this.vertex2) {
            // Need to reverse edge.
            edge._reverse();
        }
        // In some cases (eg when removing manufacturing offsets from geometry)
        // we end up in situations where a face collapses during a parallel edge merge.
        // If edges share an identical face, mark as a tab on both edge.
        const commonFace = this.incidentFaces.find(face => edge.incidentFaces.indexOf(face) >= 0);
        if (commonFace) {
            if (this.tabFaces.indexOf(commonFace) < 0) {
                this.tabFaces.push(commonFace);
                if (this.leftFace === commonFace) {
                    this._leftFace = undefined;
                }
                if (this.rightFace === commonFace) {
                    this._rightFace = undefined;
                }
            }
            if (edge.tabFaces.indexOf(commonFace) < 0) {
                edge.tabFaces.push(commonFace);
                if (edge.leftFace === commonFace) {
                    edge._leftFace = undefined;
                }
                if (edge.rightFace === commonFace) {
                    edge._rightFace = undefined;
                }
            }
            commonFace.isTab = true;
        }
        // Check that we don't create a non-manifold graph.
        if (edge.leftFace && this.leftFace) {
            // Check if either of the faces is a tab.
            if (edge.leftFace.isTab) {
                edge.tabFaces.push(edge.leftFace);
                edge._leftFace = undefined;
            }
            else if (this.leftFace.isTab) {
                this.tabFaces.push(this.leftFace);
                this._leftFace = undefined;
            }
            else if (edge.leftFace.incidentFaces.length === 1
                && this.leftFace.incidentFaces.length > 1) {
                edge.leftFace.isTab = true;
                edge.tabFaces.push(edge.leftFace);
                edge._leftFace = undefined;
            }
            else if (this.leftFace.incidentFaces.length === 1
                && edge.leftFace.incidentFaces.length > 1) {
                this.leftFace.isTab = true;
                this.tabFaces.push(this.leftFace);
                this._leftFace = undefined;
            }
            else {
                // Don't merge.
                return;
            }
        }
        if (edge.rightFace && this.rightFace) {
            // Check if either of the faces is a tab.
            if (edge.rightFace.isTab) {
                edge.tabFaces.push(edge.rightFace);
                edge._rightFace = undefined;
            }
            else if (this.rightFace.isTab) {
                this.tabFaces.push(this.rightFace);
                this._rightFace = undefined;
                // There may be a face that should be labelled as a tab.
                // Look for exactly one terminal node in dual.
            }
            else if (edge.rightFace.incidentFaces.length === 1
                && this.rightFace.incidentFaces.length > 1) {
                edge.rightFace.isTab = true;
                edge.tabFaces.push(edge.rightFace);
                edge._rightFace = undefined;
            }
            else if (this.rightFace.incidentFaces.length === 1
                && edge.rightFace.incidentFaces.length > 1) {
                this.rightFace.isTab = true;
                this.tabFaces.push(this.rightFace);
                this._rightFace = undefined;
            }
            else {
                // Don't merge.
                return;
            }
        }
        // If one of these edges has a foldAngle, apply to both.
        if (edge.foldAngle !== null) {
            if (this.foldAngle !== null) {
                if (this.foldAngle !== edge.foldAngle) {
                    throw new Error(`Attempting to merge edges with incompatible 
                        foldAngles: ${edge.foldAngle}, ${this.foldAngle}`);
                }
            }
            this.foldAngle = edge.foldAngle;
        }
        // Replace CreaseEdgeRef.edge from edge.incidentEdges.edgeLoops with this.
        edge.incidentFaces.forEach((face) => {
            face.replaceEdge(edge, this);
        });
        // Add edge's faces to this.
        if (edge.leftFace) {
            this._leftFace = edge.leftFace;
        }
        if (edge.rightFace) {
            this._rightFace = edge.rightFace;
        }
        // Merge any tab faces.
        this._tabFaces = this._tabFaces.concat(edge.tabFaces);
        // Remove CreaseEdgeRef from edge.vertex.incidentEdges.
        [edge.vertex1, edge.vertex2].forEach((vertex) => {
            vertex.removeEdge(edge);
        });
        // Merge MetaEdges.
        this._edges = this._edges.concat(edge.edges);
        this._parallelEdges = this._parallelEdges.concat(edge.parallelEdges);
        this._parallelEdgeLengths = this._parallelEdgeLengths.concat(edge._parallelEdgeLengths);
        edge.destroy();
    }
    /**
     * Merges multiple MetaEdges in series.
     * @param {MetaEdge[]} edges An edge to merge to this one.
     * @param {number} newLength: The new length of this edge.
     * Passed in edge is destroyed upon completion of merge.
     */
    mergeSerial(edges, newLength) {
        // Cannot merge serial if MetaEdges contain edges in parallel.
        if (this.edges.length > 1) {
            throw new Error('Unable to serially merge two MetaEdges: one or more edges contains edges in parallel.');
        }
        edges.forEach((edge) => {
            if (edge.edges.length > 1) {
                throw new Error('Unable to serially merge two MetaEdges: one or more edges contains edges in parallel.');
            }
        });
        if (edges.length === 0) {
            // We have merged all other parts away in an earlier step.
            this._parallelEdgeLengths[0] = newLength;
            return;
        }
        // Edges need same orientation.
        let lastEdge = this;
        const allCommonVertices = [];
        edges.forEach((edge) => {
            if (edge.vertex1 === lastEdge.vertex1 || edge.vertex2 === lastEdge.vertex2) {
                // Need to reverse edge.
                edge._reverse();
            }
            if (edge.vertex1 === lastEdge.vertex2 && edge.vertex2 === lastEdge.vertex1) {
                // Rare case that we have a slit hanging off this merging edge.
                if (edge.vertex1.incidentEdges.length === 2) {
                    allCommonVertices.push(edge.vertex1);
                }
                else {
                    allCommonVertices.push(edge.vertex2);
                }
            }
            else {
                if (edge.vertex1 === lastEdge.vertex2) {
                    allCommonVertices.push(edge.vertex1);
                }
                if (edge.vertex2 === lastEdge.vertex1) {
                    allCommonVertices.push(edge.vertex2);
                }
                if (edge.vertex1 !== lastEdge.vertex2 && edge.vertex2 !== lastEdge.vertex1) {
                    throw new Error('Unable to serially merge two MetaEdges: no vertex in common.');
                }
            }
            lastEdge = edge;
        });
        // Get adjacent faces to these edges.
        let { leftFace, rightFace } = this;
        let allTabFaces = this.tabFaces;
        edges.forEach((edge) => {
            if (leftFace && edge.leftFace && leftFace !== edge.leftFace) {
                throw new Error(`Unable to serially merge MetaEdges:
                incompatible left faces ${leftFace.toString()} ${edge.leftFace.toString()}.`);
            }
            if (rightFace && edge.rightFace && rightFace !== edge.rightFace) {
                throw new Error(`Unable to serially merge MetaEdges:
                incompatible right faces ${rightFace.toString()} ${edge.rightFace.toString()}.`);
            }
            if (!leftFace && edge.leftFace) {
                leftFace = edge.leftFace;
                leftFace.replaceEdge(edge, this);
            }
            if (!rightFace && edge.rightFace) {
                rightFace = edge.rightFace;
                rightFace.replaceEdge(edge, this);
            }
            const tabFaces = edge.tabFaces.filter(face => allTabFaces.indexOf(face) < 0);
            tabFaces.forEach((tabFace) => {
                tabFace.replaceEdge(edge, this);
            });
            allTabFaces = allTabFaces.concat(tabFaces);
        });
        // TODO: Is it possible for leftFace or rightFace to be undefined here?
        // @ts-ignore
        if (allTabFaces.indexOf(leftFace) >= 0 || allTabFaces.indexOf(rightFace) >= 0) {
            throw new Error('Unable to serially merge two MetaEdges: incompatible tab faces.');
        }
        // Remove CreaseEdgeRef.edge from edge.face.edgeLoops.
        edges.forEach((edge) => {
            if (leftFace) {
                leftFace.removeEdge(edge);
            }
            if (rightFace) {
                rightFace.removeEdge(edge);
            }
            allTabFaces.forEach((face) => {
                face.removeEdge(edge);
            });
        });
        // Update this.vertices.
        let { vertex1, vertex2 } = this;
        let firstEdge = true;
        if (allCommonVertices.indexOf(vertex1) < 0) {
            ({ vertex2 } = edges[edges.length - 1]);
            this.replaceVertex(this.vertex2, vertex2);
            vertex2.replaceEdge(edges[edges.length - 1], this);
        }
        else {
            firstEdge = false;
            ({ vertex1 } = edges[edges.length - 1]);
            this.replaceVertex(this.vertex1, vertex1);
            vertex1.replaceEdge(edges[edges.length - 1], this);
        }
        // Remove commonVertices if possible.
        allCommonVertices.forEach((vertex) => {
            if (vertex.incidentEdges.length === 2) {
                // This vertex can be totally deleted.
                if (leftFace) {
                    vertex.removeFace(leftFace);
                }
                if (rightFace) {
                    vertex.removeFace(rightFace);
                }
                vertex.removeAllEdges();
                vertex.remove();
            }
            else {
                // Deactivate this vertex.
                // Merge this vertex with nearest.
                // TODO: this is a patch for now.
                vertex.deactivated = true;
            }
        });
        // If one of these edges is a crease, set the entire edge as crease.
        // If one of these edges has a foldAngle, use it.
        const { foldAngle } = this;
        edges.forEach((edge) => {
            if (edge.isCrease) {
                if (this.isCrease && this.assignment !== edge.assignment) {
                    throw new Error(`incompatible creae assngments: ${this.assignment}, ${edge.assignment}`);
                }
                this.assignment = edge.assignment;
            }
            if (edge.foldAngle !== null) {
                if (this.foldAngle !== null && this.foldAngle !== edge.foldAngle) {
                    throw new Error(`Attempting to merge edges with incompatible 
                        foldAngles: ${edge.foldAngle}, ${foldAngle}`);
                }
                this.foldAngle = edge.foldAngle;
            }
        });
        // Merge MetaEdges.
        let allCreaseEdges = [];
        edges.forEach((edge) => {
            allCreaseEdges = allCreaseEdges.concat(edge.edges);
        });
        this._edges = this.edges.concat(allCreaseEdges);
        // Be careful that we are preserving the correct order here.
        if (firstEdge) {
            this._parallelEdges[0] = this._parallelEdges[0].concat(allCreaseEdges);
        }
        else {
            this._parallelEdges[0] = (allCreaseEdges.reverse()).concat(this.parallelEdges[0]);
        }
        this._parallelEdgeLengths[0] = newLength;
        edges.forEach((edge) => {
            edge.destroy();
        });
    }
    /**
     * Split this MetaEdge (crease) into two boundary edges.
     */
    splitParallel(face, index) {
        if (!face) {
            throw new Error('No reference face passed into MetaEdge.splitParallel()');
        }
        if (this._edges.length > 1) {
            throw new Error(`Can't split edge that has already been merged: ${this.toString()}`);
        }
        if (!this.isCrease) {
            throw new Error(`Can't split edge with assignment ${this.assignment} : ${this.toString()}`);
        }
        if (this.rightFace !== face && this.leftFace !== face) {
            throw new Error(`Edge: ${this.toString()} not adjacent to face: ${face.toString}`);
        }
        // Give CreaseEdge to new MetaEdge.
        const edge = new MetaEdge(index, this._edges[0], this.vertex1, this.vertex2);
        edge.foldAngle = this.foldAngle;
        this._edges = [];
        this._foldAngle = 0;
        this._assignment = 'B';
        // Add edge to vertices.
        const vertex1Ref = this.vertex1.incidentEdges.find(edgeRef => edgeRef.edge === this);
        const vertex2Ref = this.vertex2.incidentEdges.find(edgeRef => edgeRef.edge === this);
        if (face === this.leftFace) {
            this.vertex1.addEdgeAtIndex(edge, this.vertex1.incidentEdges.indexOf(vertex1Ref));
            this.vertex2.addEdgeAtIndex(edge, this.vertex2.incidentEdges.indexOf(vertex2Ref) + 1);
            edge._leftFace = face;
            this._leftFace = undefined;
        }
        else {
            this.vertex1.addEdgeAtIndex(edge, this.vertex1.incidentEdges.indexOf(vertex1Ref) + 1);
            this.vertex2.addEdgeAtIndex(edge, this.vertex2.incidentEdges.indexOf(vertex2Ref));
            edge._rightFace = face;
            this._rightFace = undefined;
        }
        // Replace edge in face.
        face.replaceEdge(this, edge);
        return edge;
    }
    /**
     * Reverse this MetaEdge, propagate changes to all EdgeRefs that reference this edge.
     */
    _reverse() {
        super._reverse();
        // Flip the reference to this edge from its neighboring vertices.
        [this.vertex1, this.vertex2].forEach((vertex) => {
            vertex.reverseEdge(this);
        });
        // Flip the reference to this edge from its neighboring faces.
        this.incidentFaces.forEach((face) => {
            face.reverseEdge(this);
        });
        // Reverse the order of all parallel edges.
        this.parallelEdges.forEach((parallelEdge) => {
            parallelEdge.reverse();
        });
    }
    /**
     * Remove this edge and remove all references from adjacent vertices and faces.
     */
    remove() {
        // Remove CreaseEdgeRef from this.vertex.incidentEdges.
        [this.vertex1, this.vertex2].forEach((vertex) => {
            vertex.removeEdge(this);
        });
        // Remove CreaseEdgeRef from this.face.edgeLoops.
        this.incidentFaces.forEach((face) => {
            face.removeEdge(this);
        });
        // Remove all references.
        this.destroy();
    }
    /**
     * Replace a vertex in this edge.
     * This should only be called by MetaVertex.merge() and MetaEdge.mergeSerial().
     * @private
     * @param {MetaVertex} oldVertex Old vertex being removed.
     * @param {MetaVertex} newVertex New vertex replacing old.
     */
    replaceVertex(oldVertex, newVertex) {
        if (this.vertex1 === oldVertex) {
            this._vertex1 = newVertex;
        }
        else if (this.vertex2 === oldVertex) {
            this._vertex2 = newVertex;
        }
        else {
            throw new Error(`Error in MetaEdge.replaceVertex: Vertex ${oldVertex.toString()} not found on this edge ${this.toString()}.`);
        }
    }
    /**
     * Remove a face connected to this edge.
     * This should only be called by MetaFace.remove().
     * @private
     * @param {MetaFace} face A MetaFace to remove.
     */
    removeFace(face) {
        if (this.leftFace === face) {
            this._leftFace = undefined;
        }
        else if (this.rightFace === face) {
            this._rightFace = undefined;
        }
        else {
            throw new Error('Cannot remove MetaFace, it is not attached to this MetaEdge.');
        }
    }
    propagateFoldAngle() {
        // const numCreaseEdges = utils.countIf(this.parallelEdges, parallelEdge =>
        //     parallelEdge[0].isCrease);
        this._parallelEdges.forEach((edges) => {
            // Check if this edge borders one gusset
            edges.forEach((edge) => {
                if (!edge.isCrease) {
                    return;
                }
                // TODO: we should make this more robust, also calculate direction of folding.
                if (this.tabFaces.filter(face => face.isGusset).length === 2
                    && this.incidentFaces.filter(face => !face.isTab).length > 0) {
                    // TODO: What should we do if this.foldAngle is null?
                    // @ts-ignore
                    edge.foldAngle = this.foldAngle + (180 - this.foldAngle) / 2;
                }
                else {
                    edge.foldAngle = this.foldAngle;
                }
            });
        });
    }
    toString() {
        if (!this.edges) {
            return '[]';
        }
        return `${JSON.stringify(this.edges.map(e => e.index))}`;
    }
    destroy() {
        delete this._edges;
        delete this._parallelEdges;
        delete this._parallelEdgeLengths;
        delete this._index;
        delete this._vertex1;
        delete this._vertex2;
        delete this._assignment;
        delete this._controlPoints;
        delete this._foldAngle;
        delete this._leftFace;
        delete this._rightFace;
        delete this._tabFaces;
        this._isDestroyed = true;
    }
}
exports.MetaEdge = MetaEdge;
//# sourceMappingURL=MetaEdge.js.map